/**
 * Runtime configuration loaded from /config.json before the React app mounts.
 *
 * The same FE bundle ships to every environment; env-specific values
 * (API base URL, build metadata, feature flags) are written into /config.json
 * by the deploy pipeline. All OAuth credentials live server-side (BFF pattern);
 * the frontend holds no client IDs or redirect URIs.
 */

export type RuntimeConfig = {
  api: {
    baseUrl: string;
  };
  build: {
    version: string;
    commit: string;
  };
  /**
   * Optional. When auth.devLogin is true, MSAL is skipped and a role-picker
   * "Dev Login" screen issues a synthetic session — for building/demoing
   * role-gated features without a BMO Entra test user. Written into config.json
   * ONLY for localhost + Dev; the QA and Prod pipelines never set it, so those
   * environments stay real-SSO-only.
   */
  auth?: {
    devLogin?: boolean;
    /**
     * Optional. Controls only whether the "Switch user" dev-identity dropdown
     * renders in the AppShell header — independent of auth.devLogin. Defaults
     * to hidden when false or absent.
     */
    showDevLoginInHeader?: boolean;
  };
};

const CONFIG_PATH = '/config.json';

let cached: RuntimeConfig | undefined;

export async function loadRuntimeConfig(): Promise<RuntimeConfig> {
  if (cached) return cached;
  const res = await fetch(CONFIG_PATH, { cache: 'no-store' });
  if (!res.ok) {
    throw new Error(`Failed to load ${CONFIG_PATH}: HTTP ${res.status}`);
  }
  const json = (await res.json()) as RuntimeConfig;
  assertConfigShape(json);
  cached = json;
  return cached;
}

export function getRuntimeConfig(): RuntimeConfig {
  if (!cached) {
    throw new Error(
      'getRuntimeConfig() called before loadRuntimeConfig() resolved. Call loadRuntimeConfig() in your app bootstrap.',
    );
  }
  return cached;
}

function assertConfigShape(c: unknown): asserts c is RuntimeConfig {
  if (!c || typeof c !== 'object') throw new Error('runtime config: not an object');
  const cfg = c as Partial<RuntimeConfig>;
  if (!cfg.api?.baseUrl) {
    throw new Error('runtime config: api.baseUrl missing');
  }
}
